import { computed, type InjectionKey, ref } from 'vue'
import { useDemandeAFStore } from '@/features/interface/demande/af/useDemandeAFStore.ts'
import { NatureFraisAvecQuantiteVNext, NatureFraisAnnexe} from '@/features/domaine/types/opco/Demande/AF'
import type { FraisDTO } from '@/features/api/extranet/dto/Demande/shared'
import { storeToRefs } from 'pinia'
import type { BaseCalculFrais } from '@/features/domaine/types/opco'
import { useWatchSalariesModules } from './useWatchSalariesModules'
import { dateFormatter, isDate, isNotNullUndefinedOrEmptyString, isNumber } from '@/features/shared/tools'
import type { UniqueID } from '@/features/domaine/types/techniques'
import type { ModuleDTO, SalarieDTO } from '@/features/api/extranet/dto/Demande/AF'
import {
  LibelleTypeMontantFrais,
  LibelleTypeQuantiteFrais,
} from '@/features/interface/demande/shared/components/frais/Frais.types.ts'
import type {
  FraisAnnexeModele,
  FraisAnnexesModeAffichage,
  FraisModele,
  NatureFraisAnnexeModele,
} from '@/features/interface/demande/af/steps/frais/types.ts'
import { useBatchUpdate } from '@/features/interface/demande/af/steps/frais/batchUpdate/useBatchUpdate.ts'

export type FraisAnnexesActions = ReturnType<typeof useFraisAnnexesActions>
export const AF_FRAIS_ANNEXES_ACTIONS: InjectionKey<FraisAnnexesActions> = Symbol('af-frais-externes-actions')

// Utilitaire pour formater le nom d'un salarié
const formatSalarieNom = (
  salarie: SalarieDTO,
  index: number,
): string => {
  const nomAffiché = salarie.nom || salarie.nomNaissance
  const nomComplet = [salarie.prenom, nomAffiché].filter(isNotNullUndefinedOrEmptyString).join(' ')
  return nomComplet || `Sans nom #${index + 1}`
}

// Utilitaire pour formater le nom d'un module
const formatModuleNom = (module: ModuleDTO, index: number): string => {
  const intitule = module.intitule?.trim()
  if (isNotNullUndefinedOrEmptyString(intitule)) {
    return intitule
  }
  if (isDate(module.dateDebut)) {
    const dateDebut = dateFormatter(module.dateDebut)
    if (isDate(module.dateFin)) {
      const dateFin = dateFormatter(module.dateFin)
      return `Module du ${dateDebut} au ${dateFin}`
    }
    return `Module du ${dateDebut}`
  }
  return `Module #${index + 1}`
}

export const useFraisAnnexesActions = () => {
  const store = useDemandeAFStore()
  const batchUpdate = useBatchUpdate()

  const { setupCustomUpdater } = store
  const { estTT, model, meta } = storeToRefs(store)
  const modeAffichage= ref<FraisAnnexesModeAffichage>('modules')

  const naturesFrais = computed<NatureFraisAnnexeModele[]>(() => {
    return NatureFraisAnnexe
      .filter((nature) => estTT.value || nature !== 'AchatMateriel')
      .map((nature) => {
        const baseCalcul: BaseCalculFrais = NatureFraisAvecQuantiteVNext.includes(nature) ? 'Quantite' : 'Montant'
        const labelMontant = LibelleTypeMontantFrais[nature] ?? 'Coût'
        const labelQuantite = baseCalcul === 'Quantite' ? (LibelleTypeQuantiteFrais[nature] ?? 'Nombre') : null
        return {
            nature,
            baseCalcul,
            intitule: store.lov.NatureFrais.find((item) => item.code === nature)?.intitule ?? nature,
            labelMontant,
            labelQuantite,
          }
        }
      )
    })

  const fraisAnnexes = computed<FraisAnnexeModele[]>(() => {
    if (!model.value?.fraisAnnexes?.length) return []
    if (!model.value?.formation?.modules?.length) return []
    if (!model.value?.salaries?.length) return []

    if (modeAffichage.value === 'salaries') {
      return fraisAnnexesSalaries.value
    } else {
      return fraisAnnexesModules.value
    }
  })

  const fraisAnnexesSalaries = computed<FraisAnnexeModele[]>(() =>
    model.value?.salaries?.map((salarie, index) => ({
        id: salarie.id,
        intitule: formatSalarieNom(salarie, index),
        modeAffichage: 'salaries',
        rows:
          model.value?.formation?.modules?.map((module, index) => {
            return {
              id: module.id!,
              intitule: formatModuleNom(module, index),
              frais: buildFraisModeleArray({ salarieId: salarie.id, moduleId: module.id }),
            }
          }) ?? []
      })
    ) ?? []
  )

  const fraisAnnexesModules = computed<FraisAnnexeModele[]>(
    () =>
      model.value?.formation?.modules?.map((module, index) => ({
        id: module.id,
        intitule: formatModuleNom(module, index),
        modeAffichage: 'modules',
        rows:
          model.value?.salaries?.map((salarie, index) => {
            const intitule = formatSalarieNom(salarie, index)
            return {
              id: salarie.id!,
              intitule,
              frais: buildFraisModeleArray({ salarieId: salarie.id, moduleId: module.id }),
            }
          }) ?? [],
      })) ?? [],
  )

  const findFraisAnnexe = (keys: { salarieId: UniqueID; moduleId: UniqueID }) => {
    const { salarieId, moduleId } = keys
    const fraisAnnexesList = model.value?.fraisAnnexes

    if (!fraisAnnexesList) {
      throw new Error('FraisAnnexes non initialisé')
    }

    const indexFraisAnnexes = fraisAnnexesList.findIndex((f) => f.salarieId === salarieId && f.moduleId === moduleId)

    if (indexFraisAnnexes === -1) {
      throw new Error(`Frais annexe non trouvé pour salarié ${salarieId} et module ${moduleId}`)
    }

    return {
      indexFraisAnnexes,
      fraisAnnexes: fraisAnnexesList[indexFraisAnnexes],
    }
  }

  function buildFraisModeleArray(keys: { salarieId: UniqueID; moduleId: UniqueID }) {
    const { fraisAnnexes, indexFraisAnnexes } = findFraisAnnexe(keys)
    return naturesFrais.value
      .map((n) => fraisAnnexes.frais.findIndex((f) => f.nature === n.nature)!)
      .map((indexFrais) => buildFraisModele({ indexFraisAnnexes, indexFrais }))
  }

  const buildFraisModele = (fraisIndex: { indexFraisAnnexes: number, indexFrais: number }) => {
    const { indexFraisAnnexes, indexFrais } = fraisIndex
    const frais = model.value!.fraisAnnexes![indexFraisAnnexes].frais[indexFrais]!
    const total = calculTotalFrais(frais)
    const $meta = meta.value?.fraisAnnexes?.[indexFraisAnnexes]?.frais?.[indexFrais] ?? {}
    const fraisModele = { ...frais, total, $meta }

    return new Proxy<FraisModele>(fraisModele, {
        set(target, property, value) {
          if (property === 'montant' || property === 'quantite') {
            updateFrais({
              indexFraisAnnexes,
              indexFrais,
              property,
              value,
            })
            target[property] = value
            return true
          }
          return false
        },
      },
    )
  }

  const updateFrais = setupCustomUpdater<{
    indexFraisAnnexes: number
    indexFrais: number
    property: 'montant' | 'quantite'
    value: number | null
  }>((patch, updateValues) => {
    const { indexFraisAnnexes, indexFrais, property, value } = updateValues
    const patchFrais = patch.fraisAnnexes![indexFraisAnnexes].frais[indexFrais]!
    patchFrais[property] = value
  }, true)

  const calculTotalFrais = (frais: FraisDTO) => {
    if (!isNumber(frais.montant)) return
    if (frais.baseCalcul === 'Montant') return frais.montant
    if (!isNumber(frais.quantite)) return
    return frais.montant * frais.quantite
  }

  /* Active la surveillance des salariés module */
  useWatchSalariesModules(naturesFrais)

  /* Workflow de saisie en masse */
  const showBatchUpdate = () => {
    const modules = model.value?.formation?.modules
      ?.map((module, index) => ({ id: module.id, intitule: formatModuleNom(module, index) })) ?? []

    // formatModuleNom(module, index)
    return batchUpdate.showDialog({
      natures: naturesFrais.value,
      modules,
    })
  }
  return {
    modeAffichage,
    naturesFrais,
    fraisAnnexes,
    fraisAnnexesModules, // Utilisé par la synthése
    showBatchUpdate
  }
}
